import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, MessageSquare, Send, Building2, CheckCircle2, ShieldCheck } from 'lucide-react';
import { AppShell } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import SeoHead from '../components/seo/SeoHead';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const TOPICS = [
  'General question',
  'Billing or refund',
  'Bug report',
  'Privacy / GDPR data request',
  'Partnerships',
  'Other',
];

/**
 * ContactPage — public support form. Submits to /api/support/tickets which
 * persists the ticket and (when Resend is configured) emails both the
 * support inbox and an acknowledgement to the requester.
 */
export default function ContactPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [topic, setTopic] = useState(TOPICS[0]);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [ticket, setTicket] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error('Please fill in your name, email, and message.');
      return;
    }
    if (message.trim().length < 10) {
      toast.error('Tell us a little more — at least 10 characters.');
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${BACKEND}/api/support/tickets`, {
        name: name.trim(),
        email: email.trim(),
        topic,
        message: message.trim(),
        source: 'contact-page',
      }, { timeout: 15000 });
      setTicket(res.data || { ticket_id: 'pending' });
      toast.success('Got it. We will reply within one business day.');
      setName('');
      setEmail('');
      setTopic(TOPICS[0]);
      setMessage('');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not send right now. Please try again or email help@blayzrr.com.');
    } finally {
      setSending(false);
    }
  };

  return (
    <AppShell>
      <SeoHead
        title={"Contact Blayzrr — support, privacy & partnerships"} 
        description={"Reach the Blayzrr team for support, privacy questions, or partnerships. We reply within one business day."} 
        path={"/contact"} 
      />
      <main className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-16 sm:py-20" data-testid="contact-page">
        <p className="text-[11px] font-mono-edge tracking-[0.22em] uppercase text-primary">Support</p>
        <h1 className="mt-3 font-display font-semibold text-4xl sm:text-5xl tracking-[-0.04em]">
          Talk to a human.
        </h1>
        <p className="mt-3 text-base text-muted-foreground max-w-2xl">
          Questions about a plan, an integration, a GDPR data request, or anything else &mdash; fill the form and we&rsquo;ll reply within one business day.
        </p>

        <div className="mt-10 grid gap-8 lg:grid-cols-[0.9fr_1.1fr]">
          {/* Contact channels */}
          <div className="space-y-3">
            <Channel
              Icon={Mail}
              title="Email support"
              href="mailto:help@blayzrr.com"
              value="help@blayzrr.com"
              testid="contact-channel-support"
            />
            <Channel
              Icon={MessageSquare}
              title="Privacy &amp; GDPR"
              href="mailto:help@blayzrr.com"
              value="help@blayzrr.com"
              testid="contact-channel-privacy"
            />
            <Channel
              Icon={Building2}
              title="Business &amp; partnerships"
              href="mailto:help@blayzrr.com"
              value="help@blayzrr.com"
              testid="contact-channel-business"
            />
            <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-[hsl(var(--ep-accent-soft,212_100%_96%))] px-3 py-2 text-[12px] text-primary">
              <ShieldCheck size={13} aria-hidden="true" />
              GDPR &amp; UK PECR compliant &middot; we never sell or share your data
            </div>
          </div>

          {/* Form / Success */}
          <Card
            className="rounded-2xl border border-border/70 bg-card/85 backdrop-blur"
            data-testid="contact-form-card"
          >
            <CardContent className="p-6 sm:p-7">
              {ticket ? (
                <div className="text-center py-8" data-testid="contact-success-state">
                  <div className="mx-auto w-14 h-14 rounded-2xl bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 grid place-items-center">
                    <CheckCircle2 size={28} aria-hidden="true" />
                  </div>
                  <h2 className="mt-5 font-display font-semibold text-2xl tracking-[-0.02em]">
                    Message received.
                  </h2>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Ticket <code className="px-1.5 py-0.5 rounded bg-secondary text-foreground text-[12px]">{(ticket.ticket_id || '').slice(0, 8)}</code> &middot; we&rsquo;ll reply within one business day.
                  </p>
                  {ticket.email_dispatched === false && (
                    <p className="mt-3 text-[11px] text-muted-foreground">
                      Heads up: email delivery is currently disabled in this environment, but your ticket is safely in our queue.
                    </p>
                  )}
                  <div className="mt-6 flex justify-center gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setTicket(null)}
                      data-testid="contact-send-another"
                      className="rounded-full"
                    >
                      Send another message
                    </Button>
                  </div>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="c-name" className="text-[11px] font-mono-edge tracking-[0.22em] uppercase text-muted-foreground">Your name</Label>
                      <Input id="c-name" data-testid="contact-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 h-11 rounded-xl" placeholder="Karthik V." autoComplete="name" />
                    </div>
                    <div>
                      <Label htmlFor="c-email" className="text-[11px] font-mono-edge tracking-[0.22em] uppercase text-muted-foreground">Your email</Label>
                      <Input id="c-email" data-testid="contact-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-11 rounded-xl" placeholder="you@example.com" autoComplete="email" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="c-topic" className="text-[11px] font-mono-edge tracking-[0.22em] uppercase text-muted-foreground">Topic</Label>
                    <select
                      id="c-topic"
                      data-testid="contact-topic"
                      value={topic}
                      onChange={(e) => setTopic(e.target.value)}
                      className="mt-1.5 w-full h-11 rounded-xl border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      {TOPICS.map((t) => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="c-msg" className="text-[11px] font-mono-edge tracking-[0.22em] uppercase text-muted-foreground">Message</Label>
                    <Textarea
                      id="c-msg"
                      data-testid="contact-message"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      className="mt-1.5 rounded-xl min-h-[140px]"
                      placeholder="Tell us what you’re trying to solve."
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={sending}
                    className="w-full h-11 rounded-full"
                    data-testid="contact-submit"
                  >
                    {sending ? 'Sending…' : 'Send message'}
                    {!sending && <Send size={14} className="ml-2" aria-hidden="true" />}
                  </Button>
                  <p className="text-[11px] text-muted-foreground text-center">
                    By submitting you agree to our <a href="/terms" className="underline hover:text-foreground">Terms</a> and <a href="/privacy" className="underline hover:text-foreground">Privacy Policy</a>.
                  </p>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </AppShell>
  );
}

const Channel = ({ Icon, title, href, value, testid }) => (
  <a
    href={href}
    className="flex items-start gap-3 rounded-2xl border border-border/70 bg-card/85 backdrop-blur p-4 hover:-translate-y-0.5 hover:border-primary/40 transition-[transform,border-color] duration-200"
    data-testid={testid}
  >
    <div className="mt-0.5 w-9 h-9 rounded-xl bg-[hsl(var(--ep-accent-soft,212_100%_96%))] grid place-items-center text-primary border border-primary/15">
      <Icon size={16} aria-hidden="true" />
    </div>
    <div className="min-w-0">
      <p className="text-sm font-semibold tracking-[-0.01em]" dangerouslySetInnerHTML={{ __html: title }} />
      <p className="mt-0.5 text-[13px] text-primary truncate">{value}</p>
    </div>
  </a>
);
